import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import GenerateVideoAdModal from '../dashboard/components/GenerateVideoAdModal';
import { useBusinessProfiles, type BusinessProfile } from '../../hooks/useBusinessProfiles';
import { useCreativePresets, type CreativePreset, type VideoAdPresetSettings } from '../../hooks/useCreativePresets';
import { useCreditsContext } from '../../context/CreditsContext';
import { adPlatforms, toneOptions } from '../../mocks/exampleAds';
import { VIDEO_AD_GENERATE_CREDITS, VIDEO_AD_VOICEOVER_CREDITS } from '../../config/videoAd';
import type { ImageEngineId } from '../../config/imageEngines';

interface CampaignForm {
  offer: string;
  campaignGoal: string;
  audienceOverride: string;
  platform: string;
  toneOverride: string;
  adAngle: string;
  campaignNotes: string;
  creativeDirection: string;
  stylePackIds: string[];
  imageCount: number;
}

const defaultCampaign: CampaignForm = {
  offer: '',
  campaignGoal: 'Build brand awareness',
  audienceOverride: '',
  platform: 'TikTok',
  toneOverride: '',
  adAngle: 'Problem/Solution',
  campaignNotes: '',
  creativeDirection: '',
  stylePackIds: [],
  imageCount: 1,
};

const videoGoals = [
  'Build brand awareness',
  'Promote a sale or event',
  'Drive sales / conversions',
  'Generate leads',
  'Show a product or service',
  'Retarget past visitors',
];

const videoAngles = [
  { value: 'Problem/Solution', label: 'Problem / Solution', icon: 'ri-question-answer-line' },
  { value: 'Before/After', label: 'Before / After', icon: 'ri-contrast-2-line' },
  { value: 'Offer-driven', label: 'Offer-driven', icon: 'ri-price-tag-3-line' },
  { value: 'Story-driven', label: 'Story-driven', icon: 'ri-chat-smile-2-line' },
  { value: 'Authority/Expert', label: 'Authority / Expert', icon: 'ri-shield-star-line' },
];

function ProfileSelector({
  profiles,
  selectedId,
  onSelect,
  loading,
}: {
  profiles: BusinessProfile[];
  selectedId: string;
  onSelect: (id: string) => void;
  loading: boolean;
}) {
  if (loading) return <div className="h-24 rounded-2xl border border-stone-100 bg-white animate-pulse" />;

  if (profiles.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-stone-200 bg-white px-5 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-100 shrink-0">
          <i className="ri-building-2-line text-stone-400 text-lg" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-stone-900">Create a profile first</p>
          <p className="text-xs text-stone-500 mt-0.5">Video ads use your profile to keep the script, visuals, and CTA on brand.</p>
        </div>
        <Link
          to="/dashboard/profiles/new"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-orange-500 to-amber-400 px-4 py-2 text-xs font-bold text-white hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap"
        >
          <i className="ri-add-line" />
          New Profile
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {profiles.map(profile => {
        const active = selectedId === profile.id;
        return (
          <button
            key={profile.id}
            type="button"
            onClick={() => onSelect(profile.id)}
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200 cursor-pointer ${
              active
                ? 'border-orange-300 bg-orange-50 text-orange-700 shadow-sm'
                : 'border-stone-200 bg-white text-stone-700 hover:border-orange-200 hover:bg-orange-50/40'
            }`}
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 to-amber-50 shrink-0">
              <i className="ri-building-2-line text-orange-500 text-sm" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold truncate">{profile.business_name}</div>
              <div className="text-xs text-stone-500 truncate">{profile.business_type}</div>
            </div>
            {active && <i className="ri-check-line text-orange-500 shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}

export default function VideoAdsPage() {
  const { profiles, loading: profilesLoading } = useBusinessProfiles();
  const { creditsRemaining, hasEnoughCredits } = useCreditsContext();
  const {
    presets: videoPresets,
    loading: presetsLoading,
    error: presetsError,
    createPreset,
    deletePreset,
  } = useCreativePresets<VideoAdPresetSettings>('video_ad');
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [campaign, setCampaign] = useState<CampaignForm>(defaultCampaign);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetMessage, setPresetMessage] = useState<string | null>(null);
  const [savingPreset, setSavingPreset] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  useEffect(() => {
    if (!selectedProfileId && profiles.length > 0) setSelectedProfileId(profiles[0].id);
  }, [profiles, selectedProfileId]);

  const selectedProfile = profiles.find(profile => profile.id === selectedProfileId) ?? null;
  const selectedPreset = videoPresets.find(preset => preset.id === selectedPresetId);
  const imageEngine: ImageEngineId = 'standard';
  const canGenerateVideo = selectedProfile !== null && hasEnoughCredits(VIDEO_AD_GENERATE_CREDITS);

  const updateCampaign = (field: keyof CampaignForm, value: string) => {
    setCampaign(prev => ({ ...prev, [field]: value }));
  };

  const applyPreset = (preset: CreativePreset<VideoAdPresetSettings>) => {
    setCampaign({ ...defaultCampaign, ...preset.settings.campaign });
    if (preset.business_profile_id && profiles.some(profile => profile.id === preset.business_profile_id)) {
      setSelectedProfileId(preset.business_profile_id);
    }
    setPresetMessage(`Loaded "${preset.name}".`);
  };

  const savePreset = async () => {
    if (savingPreset) return;
    const cleanName = presetName.trim();
    if (!cleanName) {
      setPresetMessage('Name the preset before saving it.');
      return;
    }
    setSavingPreset(true);
    setPresetMessage(null);
    const { error: saveErr } = await createPreset({
      name: cleanName,
      businessProfileId: selectedProfileId || null,
      settings: { campaign },
    });
    setSavingPreset(false);
    if (saveErr) {
      setPresetMessage(saveErr);
      return;
    }
    setPresetName('');
    setShowSavePreset(false);
    setPresetMessage(`Saved "${cleanName}" as a reusable preset.`);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-stone-950">Video Ads</h1>
            <p className="text-sm text-stone-500 mt-1">Build Runway video ads with optional voiceover from a focused video brief.</p>
          </div>
          <Link
            to="/dashboard/history"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-bold text-stone-700 hover:border-orange-200 hover:text-orange-600 transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-history-line text-base" />
            History
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6">
          <section className="space-y-6">
            <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <i className="ri-bookmark-3-line text-orange-500" />
                    <h2 className="text-sm font-extrabold text-stone-900 uppercase tracking-wider">Video Presets</h2>
                  </div>
                  <p className="text-sm text-stone-500 mt-1">Reuse winning hooks, angles, audiences, and visual direction.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowSavePreset(open => !open);
                    setPresetMessage(null);
                  }}
                  className="shrink-0 flex items-center gap-1.5 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-xs font-bold text-stone-600 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600 transition-all cursor-pointer"
                >
                  <i className="ri-add-line" />
                  Save
                </button>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    value={selectedPresetId}
                    onChange={event => setSelectedPresetId(event.target.value)}
                    disabled={presetsLoading || videoPresets.length === 0}
                    className="flex-1 h-11 rounded-xl border border-stone-200 bg-stone-50 px-3 text-sm font-semibold text-stone-800 disabled:text-stone-400 focus:border-orange-300 focus:ring-4 focus:ring-orange-100 outline-none"
                  >
                    <option value="">
                      {presetsLoading
                        ? 'Loading presets...'
                        : videoPresets.length === 0
                          ? 'No saved video presets yet'
                          : 'Choose a saved video preset...'}
                    </option>
                    {videoPresets.map(preset => (
                      <option key={preset.id} value={preset.id}>{preset.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => selectedPreset && applyPreset(selectedPreset)}
                    disabled={!selectedPreset}
                    className={`shrink-0 flex items-center justify-center gap-1.5 rounded-xl px-4 py-3 text-xs font-bold transition-all ${
                      selectedPreset
                        ? 'bg-stone-900 text-white hover:bg-stone-800 cursor-pointer'
                        : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                    }`}
                  >
                    <i className="ri-download-cloud-2-line" />
                    Apply
                  </button>
                </div>

                {selectedPreset && (
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold text-stone-800">{selectedPreset.name}</p>
                      <p className="truncate text-xs text-stone-400">
                        {selectedPreset.settings.campaign.platform || 'Any platform'} · {selectedPreset.settings.campaign.adAngle || 'Any angle'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        await deletePreset(selectedPreset.id);
                        setSelectedPresetId('');
                        setPresetMessage(`Deleted "${selectedPreset.name}".`);
                      }}
                      className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-stone-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                      title="Delete preset"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                )}

                {showSavePreset && (
                  <div className="rounded-xl border border-orange-100 bg-orange-50 p-3 space-y-2">
                    <input
                      type="text"
                      value={presetName}
                      onChange={event => setPresetName(event.target.value.slice(0, 60))}
                      placeholder="Preset name, e.g. Bold TikTok product reveal"
                      className="w-full rounded-xl border border-orange-200 bg-white px-3 py-2.5 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={savePreset}
                        disabled={savingPreset}
                        className="flex items-center justify-center gap-1.5 rounded-xl bg-orange-500 px-3 py-2.5 text-xs font-extrabold text-white hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <i className={savingPreset ? 'ri-loader-4-line animate-spin' : 'ri-save-3-line'} />
                        {savingPreset ? 'Saving...' : 'Save Preset'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowSavePreset(false);
                          setPresetName('');
                          setPresetMessage(null);
                        }}
                        className="rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-xs font-bold text-stone-500 hover:border-stone-300 hover:text-stone-700 cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {(presetMessage || presetsError) && (
                  <p className={`text-xs ${presetsError ? 'text-red-500' : 'text-stone-500'}`}>
                    {presetsError || presetMessage}
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-100">
                <div className="flex items-center gap-2">
                  <i className="ri-building-2-line text-orange-500" />
                  <h2 className="text-sm font-extrabold text-stone-900 uppercase tracking-wider">Profile</h2>
                </div>
                <p className="text-sm text-stone-500 mt-1">Choose the brand this video should represent.</p>
              </div>
              <div className="p-5">
                <ProfileSelector
                  profiles={profiles}
                  selectedId={selectedProfileId}
                  onSelect={setSelectedProfileId}
                  loading={profilesLoading}
                />
                {selectedProfile && (
                  <Link
                    to={`/dashboard/profiles/${selectedProfile.id}/edit`}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-stone-500 hover:text-orange-600 transition-colors"
                  >
                    <i className="ri-edit-line" />
                    Edit selected profile
                  </Link>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-100">
                <div className="flex items-center gap-2">
                  <i className="ri-movie-2-line text-orange-500" />
                  <h2 className="text-sm font-extrabold text-stone-900 uppercase tracking-wider">Video Brief</h2>
                </div>
                <p className="text-sm text-stone-500 mt-1">Set the core message before choosing video mode, motion, and audio.</p>
              </div>
              <div className="p-5 space-y-5">
                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">Offer / Topic</label>
                  <textarea
                    value={campaign.offer}
                    onChange={event => updateCampaign('offer', event.target.value)}
                    rows={4}
                    maxLength={400}
                    placeholder="e.g. Summer membership special, new product launch, weekend event, free estimate, brand awareness video..."
                    className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:border-orange-300 focus:ring-4 focus:ring-orange-100 outline-none resize-none transition-all"
                  />
                  <div className="mt-1 text-right text-xs text-stone-400">{campaign.offer.length}/400</div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">Platform</label>
                    <div className="grid grid-cols-2 gap-2">
                      {adPlatforms.map(platform => (
                        <button
                          key={platform}
                          type="button"
                          onClick={() => updateCampaign('platform', platform)}
                          className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-bold transition-all duration-200 cursor-pointer ${
                            campaign.platform === platform
                              ? 'border-orange-300 bg-orange-50 text-orange-700'
                              : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-orange-200 hover:bg-orange-50/50'
                          }`}
                        >
                          <i className={platform === 'TikTok' ? 'ri-tiktok-line' : platform === 'Instagram' ? 'ri-instagram-line' : platform === 'LinkedIn' ? 'ri-linkedin-box-line' : platform === 'Google' ? 'ri-google-line' : 'ri-facebook-circle-line'} />
                          {platform}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">Goal</label>
                    <select
                      value={campaign.campaignGoal}
                      onChange={event => updateCampaign('campaignGoal', event.target.value)}
                      className="w-full h-11 rounded-xl border border-stone-200 bg-white px-3 text-sm font-semibold text-stone-800 focus:border-orange-300 focus:ring-4 focus:ring-orange-100 outline-none"
                    >
                      {videoGoals.map(goal => <option key={goal} value={goal}>{goal}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">Audience</label>
                    <input
                      value={campaign.audienceOverride}
                      onChange={event => updateCampaign('audienceOverride', event.target.value)}
                      placeholder="e.g. busy parents, local homeowners, first-time buyers"
                      className="w-full h-11 rounded-xl border border-stone-200 bg-white px-4 text-sm text-stone-900 placeholder:text-stone-400 focus:border-orange-300 focus:ring-4 focus:ring-orange-100 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">Tone</label>
                    <select
                      value={campaign.toneOverride}
                      onChange={event => updateCampaign('toneOverride', event.target.value)}
                      className="w-full h-11 rounded-xl border border-stone-200 bg-white px-3 text-sm font-semibold text-stone-800 focus:border-orange-300 focus:ring-4 focus:ring-orange-100 outline-none"
                    >
                      <option value="">Use profile default</option>
                      {toneOptions.map(tone => <option key={tone.value} value={tone.value}>{tone.label}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">Angle</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {videoAngles.map(angle => (
                      <button
                        key={angle.value}
                        type="button"
                        onClick={() => updateCampaign('adAngle', angle.value)}
                        className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-xs font-bold transition-all duration-200 cursor-pointer ${
                          campaign.adAngle === angle.value
                            ? 'border-orange-300 bg-orange-50 text-orange-700'
                            : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-orange-200 hover:bg-orange-50/50'
                        }`}
                      >
                        <i className={`${angle.icon} text-sm`} />
                        {angle.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">Creative Direction</label>
                  <textarea
                    value={campaign.creativeDirection}
                    onChange={event => updateCampaign('creativeDirection', event.target.value)}
                    rows={4}
                    maxLength={500}
                    placeholder="Describe the visual style, scene, pacing, product shots, people, setting, or anything the video should avoid."
                    className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:border-orange-300 focus:ring-4 focus:ring-orange-100 outline-none resize-none transition-all"
                  />
                  <div className="mt-1 text-right text-xs text-stone-400">{campaign.creativeDirection.length}/500</div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-2">Extra Notes</label>
                  <textarea
                    value={campaign.campaignNotes}
                    onChange={event => updateCampaign('campaignNotes', event.target.value)}
                    rows={3}
                    maxLength={300}
                    placeholder="Optional: required wording, seasonal details, key benefits, location notes, compliance reminders..."
                    className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:border-orange-300 focus:ring-4 focus:ring-orange-100 outline-none resize-none transition-all"
                  />
                </div>
              </div>
            </div>
          </section>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-stone-200 bg-white p-5 sticky top-28">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-flashlight-line text-orange-500" />
                <h2 className="text-sm font-extrabold text-stone-900 uppercase tracking-wider">Ready To Generate</h2>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2.5">
                  <span className="font-semibold text-stone-500">Balance</span>
                  <span className="font-extrabold text-stone-900">{creditsRemaining} credits</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2.5">
                  <span className="font-semibold text-stone-500">Video</span>
                  <span className="font-extrabold text-stone-900">{VIDEO_AD_GENERATE_CREDITS}-100 credits</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2.5">
                  <span className="font-semibold text-stone-500">Voiceover</span>
                  <span className="font-extrabold text-stone-900">+{VIDEO_AD_VOICEOVER_CREDITS}</span>
                </div>
              </div>
              {!hasEnoughCredits(VIDEO_AD_GENERATE_CREDITS) && (
                <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-xs text-red-700">
                  <i className="ri-error-warning-line text-sm shrink-0" />
                  <span>You need at least {VIDEO_AD_GENERATE_CREDITS} credits to generate a video ad.</span>
                </div>
              )}
              <button
                type="button"
                disabled={!canGenerateVideo}
                onClick={() => setVideoModalOpen(true)}
                className={`mt-5 w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-extrabold transition-all duration-200 whitespace-nowrap ${
                  canGenerateVideo
                    ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                    : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-movie-2-fill text-base" />
                Create Video Ad
              </button>
              <p className="mt-3 text-xs leading-relaxed text-stone-500">
                The next step lets you choose text-to-video, image-to-video, uploaded image animation, motion style, aspect ratio, and optional voiceover.
              </p>
            </div>
          </aside>
        </div>
      </main>

      <GenerateVideoAdModal
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        businessProfile={selectedProfile}
        campaign={campaign}
        imageEngine={imageEngine}
        referenceImageData={null}
        styleLabel="Auto Mix"
      />
    </div>
  );
}
